import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function CountUp({ target, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const steps = 60; let current = 0;
        const increment = target / steps;
        const timer = setInterval(() => {
          current += increment;
          if (current >= target) { setCount(target); clearInterval(timer); }
          else setCount(Math.floor(current));
        }, 1800 / steps);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref} className="stat-number">{prefix}{count}{suffix}</span>;
}

const OPERATIONAL = [
  {
    num: '01.',
    title: 'End-to-End Ownership',
    text: 'The very basic pillar of our operational philosophy is the principle of end-to-end ownership. From the initial idea all the way to the final production, marketing and after services, everything is done in-house.',
    img: 'https://cdn.prod.website-files.com/68234acd8fb1ab421a72b174/6835a99170b24fb2b597dc79_67fcf042466b5fc4b3acb8fe_Kutezofis_202404030866%20(2)-p-2000.jpg',
  },
  {
    num: '02.',
    title: 'Cross-functional Collaboration',
    text: 'We are where the engineering mind meets the artistic soul. Collaboration and collegiality are what makes our organization unique. We value different intelligences, skills and perspectives, and foster a culture where teams and individuals from different departments seamlessly come together to create solutions with impact.',
    img: 'https://cdn.prod.website-files.com/68234acd8fb1ab421a72b174/6835a8aec12bc3ef52a06905_67fe3fdb20849ca5542d266c_kutezofis_21-03-240034%20(1)%20(1)%20(1).jpg',
  },
  {
    num: '03.',
    title: 'Dynamism and Innovation',
    text: "We want to engineer and reinvent the entire process of production and marketing to create a seamless, sustainable, reliable and enjoyable experience for our customers. That's why dynamism and innovation are at the core of our DNA.",
    img: 'https://cdn.prod.website-files.com/68234acd8fb1ab421a72b174/6835a9c48091fd9a2901a976_67fced80442ecffc8529864d_14012025-JANUARY04786%20(1)%20(1)%20(2).jpg',
  },
];

const BELIEFS = [
  { title: 'Curiosity & Courage', text: 'Curiosity sparks our ideas; courage, then, fuels our actions, empowering us to take bold, calculated risks.', img: 'https://cdn.prod.website-files.com/68234acd8fb1ab421a72b174/6835af1a4a4049d54f619524_ChatGPT%20Image%20May%2027%2C%202025%2C%2002_24_36%20PM.png' },
  { title: 'Collaboration & Respect', text: 'We celebrate our differences and see the potential they have for driving positive change.', img: 'https://cdn.prod.website-files.com/68234acd8fb1ab421a72b174/6835b5d83621a9620789e5de_ChatGPT%20Image%20May%2027%2C%202025%2C%2002_36_02%20PM.png' },
  { title: 'Transparency & Fairness', text: 'Our impact on the planet is minimized, and our practices contribute to a more just and equitable world.', img: 'https://cdn.prod.website-files.com/68234acd8fb1ab421a72b174/6835b1ca4893b574e8b55541_ChatGPT%20Image%20May%2027%2C%202025%2C%2002_34_31%20PM.png' },
];

export default function About() {
  const [heroRef, heroIn] = useInView(0.1);
  const [opRef, opIn] = useInView(0.1);
  const [beliefRef, beliefIn] = useInView(0.1);
  const [activeTab, setActiveTab] = useState(0);

  return (
    <main className="about">
      <section className="about-hero" ref={heroRef}>
        <div className={`about-hero__content ${heroIn ? 'animate-in' : ''}`}>
          <span className="about-hero__label">Who are we?</span>
          <p className="about-hero__text">At Renart, we are a team of curious individuals with diverse backgrounds and various areas of expertise, all brought together by a shared can-do attitude. By collaborating across different fields and areas of know-how, together, we develop brands with unique value propositions and engineer sustainable, seamlessly tailored shopping experiences for the evolving and dynamic needs of modern-day customers.</p>
        </div>
      </section>

     <section className="journey">
      <div className="journey__inner">
        <div className="journey__left">
          <h2 className="journey__title">Our Journey</h2>
        </div>
        <div className="journey__right">
          <p className="journey__text">
            Renart is a fully integrated group company, housing different brands under its roof.
            Our different brands are all connected by a culture of innovation, collaboration and
            a commitment to social and environmental responsibility. This shared foundation shapes
            everything we do and reflects across all functions.
          </p>
        </div>
      </div>

      <div className="spacer-xlarge" aria-hidden="true" />

      <div className="journey__stats-wrap">
        <div className="stats">
          <div className="stat">
            <div className="stat-value"><span className="stat-prefix">+</span><CountUp target={50} /></div>
            <p className="stat-label">Years of Experience</p>
          </div>
          <div className="stat">
            <div className="stat-value"><CountUp target={98} /><span className="stat-prefix">%</span></div>
            <p className="stat-label">Client Satisfaction</p>
          </div>
          <div className="stat">
            <div className="stat-value"><span className="stat-prefix">+</span><CountUp target={13} /></div>
            <p className="stat-label">Brands Created</p>
          </div>
        </div>

        <div className="journey__cta">
          <Link to="/contact" className="navbar__contact-btn">
            <span>Get In Touch</span>
            <div className="navbar__contact-circle" aria-hidden="true">
              <svg className="arrow-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.0037 9.41421L7.39712 18.0208L5.98291 16.6066L14.5895 8H7.00373V6H18.0037V17H16.0037V9.41421Z"/>
              </svg>
            </div>
          </Link>
        </div>
      </div>
    </section>

    <section className="operational" ref={opRef}>
      <div className="operational__container">

        <div className="operational__left">
          <h2 className="heading-style-h3">Operational Philosophy</h2>
          <div className="operational__mobile-image">
            {OPERATIONAL.map((item, i) => (
              <img
                key={i}
                src={item.img}
                alt={item.title}
                className={`op-image-mobile ${activeTab === i ? 'op-image-mobile--active' : ''}`}
                loading="lazy"
              />
            ))}
          </div>
          <div className="operational__tabs">
            {OPERATIONAL.map((item, i) => (
              <button
                key={i}
                className={`op-tab ${activeTab === i ? 'op-tab--active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                <div className="op-tab__header">
                  <span className="how-it-works-number">{item.num}</span>
                  <h3 className="work-process-title">{item.title}</h3>
                </div>
                <div className={`op-tab__body ${activeTab === i ? 'op-tab__body--open' : ''}`}>
                  <p className="work-process-description">{item.text}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="operational__right">
          {OPERATIONAL.map((item, i) => (
            <img
              key={i}
              src={item.img}
              alt={item.title}
              className={`op-image ${activeTab === i ? 'op-image--active' : ''}`}
              loading="lazy"
            />
          ))}
        </div>

      </div>
    </section>

    </main>
  );
}